import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Award, Trophy, GraduationCap, Sparkles, CheckCircle2,
  ArrowUpRight, Building2, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const TIERS = [
  {
    key: "verified",
    icon: ShieldCheck,
    accent: "from-ec-emerald to-emerald-700",
    name: "EthicCred Verified Institution",
    blurb: "Awarded to institutions that have successfully completed profile verification and meet EthicCred verification standards.",
    benefits: [
      "Verified Institution Badge",
      "Public Verification Page",
      "Enhanced Institutional Credibility",
      "Employer Trust Recognition",
    ],
  },
  {
    key: "certified",
    icon: Award,
    accent: "from-amber-500 to-amber-700",
    name: "EthicCred Certified Institution",
    blurb: "Awarded to institutions demonstrating excellence in student credential management and professional development.",
    benefits: [
      "Certified Institution Status",
      "QR Verifiable Certification",
      "Digital & Physical Certification Assets",
      "Institution Recognition Listing",
      "Employer Visibility",
    ],
  },
  {
    key: "top",
    icon: Trophy,
    accent: "from-violet-500 to-violet-800",
    name: "EthicCred Top Institution",
    blurb: "Awarded to outstanding institutions that demonstrate exceptional student outcomes and professional development initiatives.",
    benefits: [
      "Premium Recognition Badge",
      "Annual Award Certificate",
      "Public Recognition Directory",
      "Enhanced Brand Visibility",
      "Institutional Prestige",
    ],
  },
  {
    key: "top-placement",
    icon: Building2,
    accent: "from-sky-500 to-sky-800",
    name: "EthicCred Top Placement Institution",
    blurb: "Recognizing institutions that consistently prepare students for successful careers.",
    benefits: [
      "Placement Excellence Recognition",
      "Employer Confidence Badge",
      "Placement Achievement Showcase",
      "Enhanced Student Attraction",
      "Stronger Industry Reputation",
    ],
  },
  {
    key: "student-excellence",
    icon: GraduationCap,
    accent: "from-rose-500 to-rose-800",
    name: "EthicCred Student Excellence Institution",
    blurb: "Recognizing institutions that actively support student growth, achievements, internships, volunteering, and professional development.",
    benefits: [
      "Student Success Recognition",
      "Excellence Certification",
      "Public Trust Badge",
      "Enhanced Institutional Reputation",
    ],
  },
];

const PARTICIPATE = [
  "Build Employer Confidence",
  "Strengthen Placement Outcomes",
  "Showcase Student Success",
  "Enhance Institutional Reputation",
  "Increase Student Trust",
  "Differentiate From Competitors",
  "Demonstrate Commitment To Professional Development",
];

export default function GetRecognized() {
  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="get-recognized-page">
      <Navbar />
      <Hero />
      <TiersSection />
      <WhyParticipate />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-ec-line">
      <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 lg:py-28 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow mx-auto" data-testid="recognition-eyebrow">
            <Award className="w-3.5 h-3.5 text-ec-emerald" />
            EthicCred Institution Recognition Program
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mt-6 leading-[0.95]">
            Recognition <br />
            <span className="relative inline-block">
              That Builds Trust.
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" preserveAspectRatio="none">
                <path d="M2 8 Q 75 2 150 6 T 298 4" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-ec-ink-soft mt-7 max-w-2xl mx-auto leading-relaxed">
            Institutions shape careers. EthicCred helps recognize and showcase institutions committed to
            <span className="text-ec-ink font-semibold"> student success, professional credibility, and employability.</span>
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register?role=institution"><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2" data-testid="recognition-cta-register">Register your Institute <ArrowUpRight className="w-4 h-4" /></Button></Link>
            <Link to="/demo?audience=institution"><Button variant="outline" className="rounded-sm border-ec-ink/20 px-6 h-12 text-base" data-testid="recognition-cta-demo">Request a Demo</Button></Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TiersSection() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20" data-testid="recognition-tiers">
      <div className="text-center mb-12">
        <div className="eyebrow text-ec-emerald">Five tiers · One reputation system</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
          Choose how you want to be recognized.
        </h2>
      </div>
      <div className="space-y-8">
        {TIERS.map((t, i) => <TierBlock key={t.key} tier={t} index={i} />)}
      </div>
    </section>
  );
}

function TierBlock({ tier, index }) {
  const Icon = tier.icon;
  const reverse = index % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border border-ec-ink/10 bg-white overflow-hidden grid lg:grid-cols-12 gap-0 noise-overlay"
      data-testid={`tier-${tier.key}`}
    >
      <div className={`relative lg:col-span-4 ${reverse ? "lg:order-2" : ""} p-8 bg-gradient-to-br ${tier.accent} text-white flex flex-col justify-between min-h-[260px]`}>
        <div>
          <Icon className="w-12 h-12 opacity-90" />
          <div className="mt-4 font-mono-plex text-xs uppercase tracking-[0.2em] opacity-80">Tier {String(index + 1).padStart(2, "0")}</div>
          <h3 className="font-display text-2xl font-black leading-tight mt-2">{tier.name}</h3>
        </div>
        <div className="absolute right-3 bottom-3 opacity-15">
          <Icon className="w-32 h-32" />
        </div>
      </div>
      <div className={`lg:col-span-8 p-8 ${reverse ? "lg:order-1" : ""}`}>
        <div className="eyebrow text-ec-ink-soft">About this recognition</div>
        <p className="text-ec-ink-soft mt-2 leading-relaxed text-base">{tier.blurb}</p>
        <div className="border-t border-ec-line mt-5 pt-5">
          <div className="eyebrow text-ec-emerald mb-3">Benefits</div>
          <ul className="grid sm:grid-cols-2 gap-2">
            {tier.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm" data-testid={`tier-${tier.key}-benefit-${b.replace(/\s+/g, "-")}`}>
                <CheckCircle2 className="w-4 h-4 text-ec-emerald mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/register?role=institution"><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-5 h-10 gap-2" data-testid={`tier-${tier.key}-apply`}>Apply for this tier <ArrowUpRight className="w-3.5 h-3.5" /></Button></Link>
          <Link to="/demo?audience=institution"><Button variant="outline" className="rounded-sm border-ec-ink/20 px-5 h-10" data-testid={`tier-${tier.key}-talk`}>Talk to our team</Button></Link>
        </div>
      </div>
    </motion.article>
  );
}

function WhyParticipate() {
  return (
    <section className="bg-ec-soft border-y border-ec-line" data-testid="recognition-why-participate">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
        <div className="text-center mb-10">
          <div className="eyebrow">Why participate?</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            Seven reasons leading <br className="hidden sm:block" />
            institutions sign up.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PARTICIPATE.map((p, i) => (
            <motion.div
              key={p}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="bg-white border border-ec-line p-5 flex items-start gap-3 hover:border-ec-emerald/40 transition-colors"
              data-testid={`participate-${i + 1}`}
            >
              <Sparkles className="w-5 h-5 text-ec-emerald mt-0.5 shrink-0" />
              <div className="font-semibold text-ec-ink">{p}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-5xl mx-auto px-6 sm:px-10 py-20 text-center" data-testid="recognition-final-cta">
      <Star className="w-10 h-10 text-ec-emerald mx-auto mb-4" />
      <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
        Earn the recognition <br />
        you've been building.
      </h2>
      <p className="text-ec-ink-soft mt-4 max-w-xl mx-auto text-lg">
        Submit your institution profile, complete a short verification, and unlock public badges that employers and applicants trust.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/register?role=institution"><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2" data-testid="recognition-final-register">Register your Institute <ArrowUpRight className="w-4 h-4" /></Button></Link>
        <Link to="/demo?audience=institution"><Button variant="outline" className="rounded-sm border-ec-ink/20 px-6 h-12 text-base" data-testid="recognition-final-demo">Request a Demo</Button></Link>
        <Link to="/login"><Button variant="ghost" className="rounded-sm px-4 h-12 text-base text-ec-ink-soft" data-testid="recognition-final-signin">Sign in</Button></Link>
      </div>
    </section>
  );
}
